import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusColorPicker } from './StatusColorPicker';
import { LeadStatus } from '@/api/lead-statuses/use-lead-statuses';

const leadStatusSchema = z.object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
    label: z
        .string()
        .min(1, 'Label is required')
        .max(255, 'Label must be less than 255 characters'),
    color: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid color format'),
    sort_order: z.number().min(0, 'Sort order must be 0 or greater').optional(),
    required_contact_attempts: z.number().min(0, 'Must be 0 or greater').optional(),
});

type LeadStatusFormData = z.infer<typeof leadStatusSchema>;

interface LeadStatusFormProps {
    initialData?: LeadStatus;
    onSubmit: (data: LeadStatusFormData) => void;
    onCancel: () => void;
    isLoading?: boolean;
    isSystem?: boolean;
}

export const LeadStatusForm: React.FC<LeadStatusFormProps> = ({
    initialData,
    onSubmit,
    onCancel,
    isLoading = false,
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<LeadStatusFormData>({
        resolver: zodResolver(leadStatusSchema),
        defaultValues: {
            name: initialData?.name || '',
            label: initialData?.label || '',
            color: initialData?.color || '#3B82F6',
            sort_order: initialData?.sort_order || 0,
            required_contact_attempts: initialData?.required_contact_attempts ?? 0,
        },
    });

    const handleFormSubmit = (data: LeadStatusFormData) => {
        onSubmit(data);
    };

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <Input
                                {...field}
                                id="name"
                                placeholder="e.g., new, contacted, qualified"
                                disabled={isLoading}
                                className={errors.name ? 'border-destructive' : ''}
                            />
                        )}
                    />
                    {errors.name && (
                        <p className="text-sm text-destructive">{errors.name.message}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="label">Display Label</Label>
                    <Controller
                        name="label"
                        control={control}
                        render={({ field }) => (
                            <Input
                                {...field}
                                id="label"
                                placeholder="e.g., New, Contacted, Qualified"
                                disabled={isLoading}
                                className={errors.label ? 'border-destructive' : ''}
                            />
                        )}
                    />
                    {errors.label && (
                        <p className="text-sm text-destructive">{errors.label.message}</p>
                    )}
                </div>
            </div>

            <div className="space-y-2">
                <Label>Color</Label>
                <Controller
                    name="color"
                    control={control}
                    disabled={isLoading}
                    render={({ field }) => (
                        <StatusColorPicker
                            value={field.value}
                            isLoading={isLoading}
                            onChange={field.onChange}
                            error={!!errors.color}
                        />
                    )}
                />
                {errors.color && <p className="text-sm text-destructive">{errors.color.message}</p>}
            </div>

            <div className="space-y-2">
                <Label htmlFor="sort_order">Sort Order</Label>
                <Controller
                    name="sort_order"
                    control={control}
                    render={({ field }) => (
                        <Input
                            {...field}
                            id="sort_order"
                            type="number"
                            min="0"
                            placeholder="0"
                            disabled={isLoading}
                            onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                        />
                    )}
                />
                <p className="text-xs text-muted-foreground">Lower numbers appear first in lists</p>
            </div>

            <div className="space-y-2">
                <Label htmlFor="required_contact_attempts">Required contact attempts</Label>
                <Controller
                    name="required_contact_attempts"
                    control={control}
                    render={({ field }) => (
                        <Input
                            {...field}
                            id="required_contact_attempts"
                            type="number"
                            min={0}
                            placeholder="0"
                            disabled={isLoading}
                            value={field.value ?? 0}
                            onChange={e =>
                                field.onChange(Math.max(0, parseInt(e.target.value, 10) || 0))
                            }
                        />
                    )}
                />
                <p className="text-xs text-muted-foreground">
                    When &gt; 0, a lead must have at least this many contact attempts before this
                    status can be selected.
                </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" disabled={!isValid || isLoading}>
                    {isLoading ? 'Saving...' : initialData ? 'Update Status' : 'Create Status'}
                </Button>
            </div>
        </form>
    );
};
